const get = require('lodash/get')
const api = require('./api')
const { EntityApi } = require('../common')
const { wrapEntityApi } = require('../utils/wrap-api')
const { resolveReadSource } = require('../tools/resolve-read-source')
const FunderPgAdapter = require('./data-access/funder-pg-adapter')

// Kept under their original names for module.exports (data-ops and other
// direct raw-function consumers rely on `.list`/`.get` here).
const rawMethods = {
  save: api.save,
  get: api.get,
  list: api.list
}

// Same functions, but under names that won't shadow the dispatching
// `list`/`get` class methods once wrapped and assigned onto `this` below.
const legacyMethods = {
  save: api.save,
  getLegacy: api.get,
  listLegacy: api.list
}

const FunderPouchAdapter = require('./data-access/funder-pouch-adapter')

class FundersApi extends EntityApi {
  // `pgConnection` is only ever passed from inside agave (see api/index.js) —
  // that's what makes `this.pgAdapter` available there. Callers that want the
  // Postgres-backed reads (the agave funder routes) use
  // `funders.pgAdapter.listForPrograms` directly rather than wrapper methods
  // here; every other caller (browser, lambdas, data-ops, the sync job)
  // constructs FundersApi without one and is unaffected.
  constructor (state, restAdapter, pgConnection, agaveAdapter, logger) {
    const { user, locationsDB } = state
    const adapter = new FunderPouchAdapter(user, locationsDB, restAdapter)
    super(adapter)

    this.state = state
    this.agaveAdapter = agaveAdapter

    // TODO: remove this when all raw methods have been removed
    const apiMethods = wrapEntityApi(legacyMethods, state)
    Object.assign(this, apiMethods)

    if (pgConnection) {
      const username = get(state, 'user.name')
      this.pgAdapter = new FunderPgAdapter(pgConnection, username, logger)
    }
  }

  // Two-way dispatch: 'agave' for an online-only browser, 'pouch' for
  // everyone else — and for agave's own fs-api, which has no transport, so
  // the handler serving `/funder` answers from couch rather than calling
  // back out to itself. No 'pg' branch here: the pg reads belong to the
  // agave controllers, which use `this.pgAdapter` directly.
  readSource () {
    return resolveReadSource({
      domain: 'funder',
      state: this.state,
      hasAgaveTransport: !!this.agaveAdapter
    })
  }

  // As on ProgramApi, `unfiltered` is not a client-decidable option: in-process
  // callers may need every tenant's funders, but no client can ask for it —
  // the agave route's zod schema declares only `programIds` and its controller
  // scopes to the user's own programs.
  async list (opts = {}) {
    if (this.readSource() === 'agave') {
      return this.agaveAdapter.get('funder', opts)
    }
    return this.listLegacy(opts)
  }

  async get (id, opts = {}) {
    if (this.readSource() === 'agave') {
      return this.agaveAdapter.get(`funder/${id}`, opts)
    }
    return this.getLegacy(id, opts)
  }
}

module.exports = rawMethods
module.exports.FundersApi = FundersApi
module.exports.FunderPgAdapter = FunderPgAdapter
